'use client'

import { useActiveItemIndexStore } from '@fukku/slideshow/hooks/useActiveItemIndexStore/useActiveItemIndexStore'
import { useSlideshowBullets } from '@fukku/slideshow/hooks/useSlideshowBullets/useSlideshowBullets'
import { useSlideshowContext } from '@fukku/slideshow/hooks/useSlideshowContext/useSlideshowContext'
import { useSlideshowLoop } from '@fukku/slideshow/hooks/useSlideshowLoop/useSlideshowLoop'
import { SlideshowFromDirection } from '@fukku/slideshow/types/Slideshow'
import { useDocumentDirection } from 'hooks/useDocumentDirection/useDocumentDirection'
import type { MouseEvent } from 'react'

export const useSlideshowNavigation = () => {
	const { slideshowRef, nItems } = useSlideshowContext()
	const { updateBulletsByDirection } = useSlideshowBullets()
	const { handleLoop } = useSlideshowLoop()
	const { index: activeItemIndex, rollIndex: rollActiveItemIndex } =
		useActiveItemIndexStore()
	const { isRtl } = useDocumentDirection()

	function getRtlDirection(
		direction: SlideshowFromDirection
	): SlideshowFromDirection {
		let rtlDirection: SlideshowFromDirection = direction

		if (isRtl) {
			if (direction === SlideshowFromDirection.Left) {
				rtlDirection = SlideshowFromDirection.Right
			} else {
				rtlDirection = SlideshowFromDirection.Left
			}
		}

		return rtlDirection
	}

	const onNavigation = (fromDirection: SlideshowFromDirection): void => {
		const onGoingSlideChange =
			slideshowRef.current?.style.getPropertyValue(
				'--slideshow-transition-duration'
			) !== '0ms'
		if (onGoingSlideChange) {
			return
		}

		const toPreviousSlide = fromDirection === SlideshowFromDirection.Left
		const isFirstItemActive = activeItemIndex === 0
		const isLastItemActive = activeItemIndex === nItems - 1

		const shouldLoop = toPreviousSlide ? isFirstItemActive : isLastItemActive
		if (shouldLoop) {
			handleLoop(fromDirection, true)
		} else {
			rollActiveItemIndex(fromDirection)
		}

		updateBulletsByDirection(fromDirection)
	}

	const onArrowClick = (
		event: MouseEvent<HTMLButtonElement>,
		direction: SlideshowFromDirection
	): void => {
		event.preventDefault()
		event.stopPropagation()
		const rtlDirection = getRtlDirection(direction)
		onNavigation(rtlDirection)
	}

	return {
		onArrowClick,
	}
}
